import React from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import InfoIcon from '@mui/icons-material/Info';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import { Box, Grid, IconButton, Rating, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../../utils/responsiveMedia';
import SalonPrimaryActions from '../../../client/salonDetails/SalonPrimaryActions';
import SalonPublicButton from '../../salonPublicAppearance/SalonPublicButton';
import SalonPublicStyleScope from '../../salonPublicAppearance/SalonPublicStyleScope';


const IDENTITY_VARIANTS = new Set([
  'immersive',
  'split',
  'compact',
  'institutional',
  'dark',
]);

const SalonTemplateIdentity = ({
  data,
  variant = 'immersive',
  visible = true,
  mode = 'public',
  onBack,
  onToggleFavorite,
  onOpenMobileInfo,
  onOpenHeaderInfo,
  onBook,
  onContact,
  clientChatEnabled,
  bookingAction = null,
}) => {
  const { t } = useTranslation();
  const { layout, tokens: theme } = useSalonPublicTheme();
  const identityVariant = IDENTITY_VARIANTS.has(variant) ? variant : 'immersive';
  const headerLayout = layout.tokens.header;
  const contentAlignment = layout.tokens.content.alignment;
  const contentAlignItems = contentAlignment === 'center'
    ? 'center'
    : contentAlignment === 'end' ? 'flex-end' : 'flex-start';
  const compact = identityVariant === 'compact' || headerLayout.composition === 'compact';
  const centered = headerLayout.composition === 'centered';
  const preview = mode === 'preview';
  const actionProps = {
    disabled: preview,
    'aria-disabled': String(preview),
  };
  const resolvedBookingAction = !preview && typeof onBook === 'function'
    ? (
      <SalonPrimaryActions
        centered={centered}
        clientChatEnabled={clientChatEnabled}
        onBook={onBook}
        onContact={onContact}
        bookLabel={t('landing_salon_details.book_appointment')}
      />
    )
    : bookingAction;

  if (!visible || !data?.name) return null;

  const hasRating = Number.isFinite(Number(data.averageRating))
    && Number(data.reviewCount) > 0;
  const mapsHref = data.latitude != null && data.longitude != null
    ? `https://www.google.com/maps?q=${data.latitude},${data.longitude}`
    : null;
  const overlay = headerLayout.surface === 'brand'
    ? `linear-gradient(135deg, ${alpha(theme.salon.primary, 0.9)} 0%, ${alpha(theme.salon.secondary, 0.82)} 100%)`
    : headerLayout.surface === 'light'
      ? 'linear-gradient(135deg, rgba(45, 45, 45, 0.62) 0%, rgba(255, 255, 255, 0.18) 100%)'
      : `linear-gradient(135deg, rgba(0, 0, 0, 0.70) 0%, ${alpha(theme.salon.secondary, 0.30)} 55%, ${alpha(theme.salon.primary, 0.55)} 100%)`;

  return (
    <SalonPublicStyleScope
      sectionKey="header"
      component="header"
      data-testid="salon-public-hero"
      data-salon-template-block="identity"
      data-variant={identityVariant}
      data-composition={headerLayout.composition}
      data-surface={headerLayout.surface}
      data-behavior={headerLayout.behavior}
      data-alignment={contentAlignment}
      sx={{
        position: headerLayout.behavior === 'sticky' ? 'sticky' : 'relative',
        top: headerLayout.behavior === 'sticky' ? 0 : 'auto',
        width: '100%',
        minHeight: compact ? '140px' : '180px',
        [HIGH_DENSITY_TABLET_MEDIA]: { minHeight: 'clamp(280px, 22vh, 360px)' },
        backgroundColor: theme.salon.background,
        backgroundImage: data.heroPhoto ? `url(${data.heroPhoto})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderBottom: `1px solid ${theme.colors.gray[300]}`,
        zIndex: 1000,
        flexShrink: 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: overlay,
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          zIndex: 1,
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          p: { xs: 2, md: 3 },
        }}
      >
        <SalonPublicButton
          preserveLegacyStyle
          variant="text"
          size="medium"
          startIcon={<ChevronLeftIcon />}
          onClick={onBack}
          {...actionProps}
          sx={{
            color: theme.salon.onPrimary,
            backgroundColor: theme.salon.primary,
            minHeight: 44,
            backdropFilter: 'blur(10px)',
            px: 2,
            py: 1,
            borderRadius: '8px',
            fontWeight: 600,
            '&:hover': {
              color: theme.salon.onPrimary,
              backgroundColor: theme.salon.primary,
              boxShadow: '0 4px 12px rgba(0,0,0,0.24)',
            },
            '&.Mui-disabled': {
              color: theme.salon.onPrimary,
              backgroundColor: theme.salon.primary,
              opacity: 1,
            },
          }}
        >
          {t('actions.back')}
        </SalonPublicButton>

        <Stack direction="row" spacing={1.5}>
          <IconButton
            aria-label={t('landing_salon_details.favorite')}
            onClick={onToggleFavorite}
            {...actionProps}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(10px)',
              borderRadius: '8px',
              p: 1.5,
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.5)', transform: 'scale(1.1)' },
            }}
          >
            {data.isFavorite ? <FavoriteIcon sx={{ color: '#ef4444' }} /> : <FavoriteBorderIcon />}
          </IconButton>
          <IconButton
            aria-label={t('landing_salon_details.informations')}
            onClick={onOpenMobileInfo}
            {...actionProps}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(10px)',
              borderRadius: '8px',
              p: 1.5,
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
            }}
          >
            <InfoIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          px: { xs: 2, md: 4 },
          pb: { xs: 2, md: 4 },
          [HIGH_DENSITY_TABLET_MEDIA]: { px: 7, pb: 5 },
        }}
      >
        <Grid container spacing={centered ? 3 : 5} alignItems="stretch">
          <Grid item xs={12} md={centered ? 12 : 6}>
            <Stack
              spacing={1.5}
              alignItems={contentAlignItems}
              justifyContent="center"
              sx={{ height: '100%', maxWidth: centered ? 720 : '90%', mx: centered ? 'auto' : 0 }}
            >
              <Typography
                data-salon-text-role="title"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '1.5rem', md: '2.5rem' },
                  textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                  textAlign: contentAlignment,
                }}
              >
                {data.name}
              </Typography>

              {hasRating ? (
                <Stack
                  data-salon-text-role="content"
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ px: 2, py: 1, borderRadius: '20px', bgcolor: 'rgba(255,255,255,0.15)' }}
                >
                  <Rating
                    value={Number(data.averageRating)}
                    precision={0.1}
                    readOnly
                    size="small"
                    aria-label={t('landing_salon_details.reviews_count', { count: data.reviewCount })}
                    sx={{ '& .MuiRating-icon': { color: '#FFD700' } }}
                  />
                  <Typography sx={{ color: 'white', fontWeight: 600 }}>
                    {Number(data.averageRating).toFixed(1)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {t('landing_salon_details.reviews_count_short', { count: data.reviewCount })}
                  </Typography>
                </Stack>
              ) : null}

              {data.description ? (
                <Typography
                  data-salon-text-role="content"
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    color: 'white',
                    lineHeight: 1.7,
                    textAlign: contentAlignment,
                    textShadow: '0 2px 8px rgba(0,0,0,0.45)',
                  }}
                >
                  {data.description}
                </Typography>
              ) : null}

              {data.address ? (
                <Stack
                  data-salon-text-role="content"
                  component={mapsHref ? 'a' : 'div'}
                  href={!preview ? mapsHref : undefined}
                  target={mapsHref && !preview ? '_blank' : undefined}
                  rel={mapsHref && !preview ? 'noopener noreferrer' : undefined}
                  aria-disabled={mapsHref ? String(preview) : undefined}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    color: 'white',
                    textDecoration: 'none',
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: '1rem', mt: 0.2 }} />
                  <Typography variant="body2" sx={{ color: 'inherit' }}>{data.address}</Typography>
                </Stack>
              ) : null}

              {data.phone ? (
                <Stack
                  data-salon-text-role="content"
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ display: { xs: 'none', md: 'flex' }, color: 'white' }}
                >
                  <PhoneIcon sx={{ fontSize: '1rem' }} />
                  <Typography variant="body2" sx={{ color: 'inherit' }}>{data.phone}</Typography>
                </Stack>
              ) : null}

              <SalonPublicButton
                preserveLegacyStyle
                variant="outlined"
                size="small"
                onClick={onOpenHeaderInfo}
                {...actionProps}
                sx={{
                  display: { xs: 'inline-flex', md: 'none' },
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                }}
              >
                {t('landing_salon_details.more_info')}
              </SalonPublicButton>
            </Stack>
          </Grid>

          {resolvedBookingAction ? (
            <Grid item xs={12} md={centered ? 12 : 6} sx={{ display: { xs: 'none', md: 'block' } }}>
              {resolvedBookingAction}
            </Grid>
          ) : null}
        </Grid>
      </Box>
    </SalonPublicStyleScope>
  );
};

export default SalonTemplateIdentity;
